/**
 * Адресная книга сотрудников (company-structure): список с группировкой по подразделению,
 * поиском по ФИО и фильтром по подразделению; колонка статуса обучения — худший статус сотрудника
 * из журнала сроков (тот же цвет, что у его строки в разделе «Обучение»). Список ПОСТРАНИЧНЫЙ
 * (issue #14): по 20 сотрудников, навигация — общий
 * `Pagination`. Поиск, фильтр по подразделению и тумблер деактивированных выполняются НА СЕРВЕРЕ
 * по всему ростеру (иначе сотрудник за пределами страницы не находился бы); группировка по
 * подразделению остаётся, но применяется к строкам текущей страницы.
 *
 * Создание сотрудника (`CreateEmployeeForm`, реорганизована по макету
 * p3-sout-commission §3, волна 3): подразделение — строгий выбор из СУЩЕСТВУЮЩИХ (`listDepartments`),
 * должность — выбор существующей ИЛИ ввод новой (`PositionSelect allowCreate`, centrika-docs!62:
 * новая должность создаётся вместе с сотрудником, find-or-create на сервере); новое рабочее место
 * резолвится сервером по паре (отдел, должность). Сменное рабочее место — выбор сменщика из существующих
 * сотрудников: подразделение/должность берутся от его рабочего места (та же строка пульта).
 * Работает через scope api.
 */
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { UserPlus } from 'lucide-react'
import {
  type CompanyScopeApi,
  type CreateEmployeeFormInput,
  type Department,
  type Employee,
  type JournalResponse,
  type JournalRow,
  type Workplace,
} from '@/lib/api'
import { getErrorMessage, fieldError } from '@/lib/errors'
import { validShiftPartners, validateFio } from '@/lib/validation'
import { isNotFutureDate, isDateOrderValid } from '@/lib/date-order'
import { PAGE_SIZE, pageCount } from '@/lib/pagination'
import { Pagination } from '@/design-system/components/data/pagination'
import { Panel } from '@/design-system/components/layout/panel'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Field } from '@/design-system/components/primitives/field'
import { Input } from '@/design-system/components/primitives/input'
import { EducationSelect } from '@/company/education-select'
import { DateInput } from '@/design-system/components/primitives/date-input'
import { SnilsInput } from '@/design-system/components/primitives/snils-input'
import { SNILS_MESSAGE } from '@/lib/snils'
import { isValidSnils } from '@/lib/validation'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { Switch } from '@/design-system/components/primitives/switch'
import { Checkbox } from '@/design-system/components/primitives/checkbox'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/components/primitives/select'
import { Dialog, DialogContent } from '@/design-system/components/primitives/dialog'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableGroupRow,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { PositionSelect, invalidatePositionsCache } from './position-select'
import {
  DepartmentSelect,
  DEPARTMENT_SELECT_EMPLOYEE_PLACEHOLDER,
  DEPARTMENT_SELECT_CHOOSE_FROM_LIST,
} from './department-select'
import { normalizePersonName } from '@/lib/name-case'
import { UNSPECIFIED_DEPARTMENT } from '@/lib/department'

const ALL = 'all'
interface CreateState {
  fullName: string
  gender: 'm' | 'f'
  birthDate: string
  snils: string
  email: string
  /** Телефон — необязательное поле профиля (p3-sout-commission D1). */
  phone: string
  education: string
  hasDisability: boolean
  /**
   * Подразделение (centrika-docs!70) — единый паттерн `DepartmentSelect` в отложенном режиме
   * (`deferCreate`), как в форме нового рабочего места СОУТ: акцентное «+ Новое подразделение»
   * вверху списка, ввод нового заменяет список полем и ссылкой «Выбрать из списка». Наименование
   * хранится в `department` (свободный текст либо имя выбранного существующего); `departmentId`
   * задан ТОЛЬКО когда выбрано существующее подразделение — тогда на сервер уходит `departmentId`,
   * иначе `newWorkplace.departmentName` (серверный find-or-create). При `isShift` с выбранным
   * сменщиком поле показывается read-only из его места — см. `derivedWorkplace`.
   */
  department: string
  /** Id выбранного существующего подразделения; пусто — когда введено новое наименование. */
  departmentId: string
  /**
   * Должность — выбор существующей из справочника ИЛИ ввод новой (`PositionSelect allowCreate`,
   * centrika-docs!62): новая должность создаётся вместе с сотрудником (find-or-create на сервере),
   * совпадение с существующей подставляется автоматически. Хранится текстом.
   */
  position: string
  /** Дата приёма на работу — необязательная (§10.1). */
  hiredOn: string
  /** Разряд — необязательный, без гейта по типу профессии (design D5). */
  rank: string
  /** Сменное рабочее место — сменщики обязательны, только если включено. */
  isShift: boolean
  shiftPartnerIds: string[]
}

const EMPTY_CREATE: CreateState = {
  fullName: '',
  gender: 'm',
  birthDate: '',
  snils: '',
  email: '',
  phone: '',
  education: '',
  hasDisability: false,
  department: '',
  departmentId: '',
  position: '',
  hiredOn: '',
  rank: '',
  isShift: false,
  shiftPartnerIds: [],
}

/**
 * Префилл состояния формы из существующего сотрудника (режим правки, p2-ot-section §9.4).
 * `departmentId` восстанавливается по текущему рабочему месту сотрудника (`workplaces` — тот же
 * список, что грузит контейнер) — денормализованный `Employee.departmentName` id не несёт.
 */
function stateFromEmployee(e: Employee, workplaces: Workplace[]): CreateState {
  return {
    fullName: e.fullName,
    gender: e.gender,
    birthDate: e.birthDate ?? '',
    snils: e.snils ?? '',
    email: e.email ?? '',
    phone: e.phone ?? '',
    education: e.education ?? '',
    hasDisability: e.hasDisability,
    department: e.departmentName ?? '',
    departmentId: workplaces.find((w) => w.id === e.workplaceId)?.departmentId ?? '',
    position: e.position,
    hiredOn: e.hiredOn ?? '',
    rank: e.rank ?? '',
    isShift: e.isShift,
    shiftPartnerIds: e.shiftPartners.map((p) => p.id),
  }
}

/**
 * Единая форма сотрудника (§10.1): СОЗДАНИЕ (по умолчанию) и ПРАВКА (`initial` задан,
 * p2-ot-section §9.4 «Изменить информацию о сотруднике»). Экспортирована для переиспользования
 * вне адресной книги: шаг 2 мастера обучения (p2-service-training, D8) добавляет сотрудника той
 * же формой; карточка сотрудника (employee-card-loader) правит той же формой. Без `initial`
 * поведение идентично прежнему create — существующие вызовы не ломаются.
 */
export function CreateEmployeeForm({
  api,
  workplaces,
  employees,
  departments,
  onCreated,
  onCancel,
  initial,
  header,
  departmentOptional = false,
}: {
  api: CompanyScopeApi
  workplaces: Workplace[]
  /** Существующие сотрудники — источник для выбора сменщиков (§10.1). */
  employees: Employee[]
  /**
   * Существующие подразделения — источник строгого выбора «Подразделение» (p3-sout-commission
   * §3.1/D4): в этой форме подразделение выбирается ТОЛЬКО из уже заведённых записей (создание
   * нового подразделения здесь недоступно — для этого есть вкладка «Рабочее место без сотрудника»
   * / импорт структуры).
   */
  departments: Department[]
  /** Резульат submit: созданный ИЛИ обновлённый сотрудник + был ли создан новый workplace. */
  onCreated: (employee: Employee, createdWorkplace: boolean) => void
  /**
   * Необязательная «Отмена». Когда выход из формы есть у самого контейнера, отдельная «Отмена» в
   * форме не нужна — не передаём проп, и кнопка не рендерится. Так сделано в модалке «Добавить в
   * структуру» (F2-17: закрывается Escape/кликом-вне/общей кнопкой) и на экране создания
   * сотрудника в диалоге выбора под программу обучения (centrika-docs,
   * component.create-training-order.programs-and-employees, MR !19: выход — кнопка «Вернуться к
   * выбору» этого экрана). Контейнеры, которые проп передают, работают как прежде.
   */
  onCancel?: () => void
  /** Если задан — форма в режиме ПРАВКИ (префилл + submit через updateEmployee). */
  initial?: Employee
  /**
   * Заголовок карточки формы. Задаётся контейнером, когда форма — единственное содержимое
   * диалогового окна и её карточка служит его видимым заголовком: страница «Сотрудники»
   * (centrika-docs!97) открывает форму в окне с заголовком «Добавить сотрудника». Без пропа —
   * прежние подписи («Новый сотрудник» / «Изменить сотрудника»), остальные вызовы не меняются.
   */
  header?: string
  /**
   * Подразделение необязательно (training-listeners-count; ТЗ п.1.3): в заявке на обучение
   * достаточно ФИО и должности — без подразделения сотрудник попадает в «Не указано». Остальные
   * формы (структура, СОУТ, профриски) подразделение по-прежнему требуют.
   */
  departmentOptional?: boolean
}) {
  const isEditing = initial != null
  const [form, setForm] = useState<CreateState>(
    initial ? stateFromEmployee(initial, workplaces) : EMPTY_CREATE,
  )
  const [error, setError] = useState<unknown>(null)
  const [submitting, setSubmitting] = useState(false)
  // Сам себе сменщиком быть нельзя — в режиме правки исключаем редактируемого из выбора.
  const partnerCandidates = employees.filter((e) => e.id !== initial?.id)
  // Правка (D4): если подразделение/должность НЕ менялись, сохраняем ссылку на ТО ЖЕ рабочее
  // место (`workplaceId`), а не создаём новое (`newWorkplace` всегда минтит новую строку места —
  // см. `resolveWorkplace`, backend не трогаем). Иначе КАЖДОЕ сохранение формы (даже правка одного
  // лишь телефона) плодило бы дубли рабочих мест. Базовая привязка берётся из исходного сотрудника
  // (`initial`+`workplaces`), а не из текущего состояния формы — сравнение идёт со стабильным
  // «было при открытии формы».
  const initialWorkplaceId = initial?.workplaceId
  const initialDepartmentId = initial
    ? workplaces.find((w) => w.id === initial.workplaceId)?.departmentId
    : undefined
  const initialPosition = initial?.position

  function set<K extends keyof CreateState>(key: K, value: CreateState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function togglePartner(id: string, checked: boolean) {
    setForm((f) => ({
      ...f,
      shiftPartnerIds: checked
        ? [...f.shiftPartnerIds, id]
        : f.shiftPartnerIds.filter((p) => p !== id),
    }))
  }

  const shiftValid = validShiftPartners(form.isShift, form.shiftPartnerIds.length)
  // Сменное место (§3.1/D3, p3-sout-commission): подразделение и должность нового сотрудника
  // берутся от рабочего места выбранного сменщика — «не позволять задать противоречащие значения»
  // реализовано так: выбранные сменщики должны делить ровно ОДНО рабочее место; при выборе людей
  // с разными местами — конфликт, поле остаётся невалидным до исправления выбора.
  const selectedPartners = form.isShift
    ? partnerCandidates.filter((e) => form.shiftPartnerIds.includes(e.id))
    : []
  const partnerWorkplaceIds = Array.from(new Set(selectedPartners.map((e) => e.workplaceId)))
  const shiftWorkplaceConflict = form.isShift && partnerWorkplaceIds.length > 1
  const derivedWorkplace =
    form.isShift && partnerWorkplaceIds.length === 1 ? selectedPartners[0] : undefined
  // Дубль (centrika-docs!70, как в add-workplace-form): наименование введено вручную (без
  // выбора существующего, departmentId пуст), но совпадает с уже заведённым подразделением —
  // выбрать нужно существующее, а не плодить одноимённое.
  const departmentDuplicate =
    form.departmentId === '' &&
    form.department.trim() !== '' &&
    departments.some(
      (d) => d.name.trim().toLowerCase() === form.department.trim().toLowerCase(),
    )
  const departmentValid =
    (departmentOptional || form.department.trim() !== '') && !departmentDuplicate
  const workplaceValid = form.isShift
    ? derivedWorkplace != null && !shiftWorkplaceConflict
    : departmentValid && form.position.trim() !== ''
  // ФИО — минимум 3 слова, без инициалов (§15 НФТ, зеркало BE domain.ValidFIO/validateEmployee).
  // Ошибка показывается только когда поле непустое — пустое ловится обычным required.
  const fioInvalid = form.fullName.trim() !== '' && !validateFio(form.fullName)
  // Порядок дат (§15 НФТ): дата рождения и дата приёма не в будущем; приём не раньше рождения.
  const birthFuture = !isNotFutureDate(form.birthDate)
  const hiredFuture = !isNotFutureDate(form.hiredOn)
  const hiredBeforeBirth = !isDateOrderValid(form.birthDate, form.hiredOn)
  const birthError = birthFuture ? 'Дата рождения не может быть в будущем' : undefined
  // СНИЛС (centrika-docs!46): поле необязательное, но ЗАПОЛНЕННОЕ обязано быть корректным —
  // 11 цифр и контрольная сумма (разделители маски проверка не учитывает).
  const snilsError =
    form.snils.trim() !== '' && !isValidSnils(form.snils) ? SNILS_MESSAGE : undefined
  const hiredError = hiredFuture
    ? 'Дата приёма не может быть в будущем'
    : hiredBeforeBirth
      ? 'Дата приёма не может быть раньше даты рождения'
      : undefined
  const datesValid = !birthFuture && !hiredFuture && !hiredBeforeBirth
  const shiftPartnerError = !shiftValid
    ? 'Укажите хотя бы одного сотрудника'
    : shiftWorkplaceConflict
      ? 'Выбранные сотрудники должны работать на одном рабочем месте'
      : undefined
  const valid =
    form.fullName.trim() !== '' &&
    !fioInvalid &&
    !snilsError &&
    datesValid &&
    workplaceValid &&
    shiftValid

  const unchangedWorkplace =
    isEditing &&
    !form.isShift &&
    initialWorkplaceId != null &&
    form.departmentId === initialDepartmentId &&
    form.position.trim() === initialPosition

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    if (!valid) return
    setSubmitting(true)
    const createdNewWorkplace = !form.isShift && !unchangedWorkplace
    const input: CreateEmployeeFormInput = {
      fullName: form.fullName.trim(),
      gender: form.gender,
      birthDate: form.birthDate,
      snils: form.snils.trim(),
      email: form.email.trim() || undefined,
      phone: form.phone.trim() || undefined,
      education: form.education.trim() || undefined,
      hasDisability: form.hasDisability,
      hiredOn: form.hiredOn || undefined,
      rank: form.rank.trim() || undefined,
      isShift: form.isShift,
      shiftPartnerIds: form.isShift ? form.shiftPartnerIds : undefined,
      ...(derivedWorkplace
        ? { workplaceId: derivedWorkplace.workplaceId }
        : unchangedWorkplace
          ? { workplaceId: initialWorkplaceId }
          : {
              newWorkplace: form.departmentId
                ? { departmentId: form.departmentId, position: form.position.trim() }
                : {
                    departmentName: form.department.trim() || UNSPECIFIED_DEPARTMENT,
                    position: form.position.trim(),
                  },
            }),
    }
    try {
      const saved = isEditing
        ? await api.updateEmployee(initial.id, input)
        : await api.createEmployee(input)
      // Могла быть заведена новая должность вместе с сотрудником (centrika-docs!62) — сбрасываем
      // модульный кэш справочника, чтобы другие открытые формы (в т.ч. строгие) увидели её.
      invalidatePositionsCache()
      onCreated(saved, createdNewWorkplace)
    } catch (err) {
      setError(err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Card
      header={header ?? (isEditing ? 'Изменить сотрудника' : 'Новый сотрудник')}
      className="mb-4"
    >
      <form onSubmit={submit} noValidate>
        {error ? (
          <Alert variant="danger" className="mb-4">
            {getErrorMessage(error, 'Не удалось создать сотрудника')}
          </Alert>
        ) : null}

        {/* Блок без заголовка (p3-sout-commission §3.1): ФИО на всю ширину, затем подразделение +
            должность, затем сменное рабочее место. */}
        <Field
          label="ФИО"
          className="my-0"
          required
          hint="Полностью, минимум 3 слова — без инициалов"
          error={
            fieldError(error, 'fullName') ??
            (fioInvalid ? 'Проверьте ФИО (3 слова, без инициалов)' : undefined)
          }
        >
          {(props) => (
            <Input
              {...props}
              value={form.fullName}
              onChange={(e) => set('fullName', e.target.value)}
              // «Иванов Иван Иванович» — на уходе с поля (names-and-positions-normalization).
              onBlur={() => set('fullName', normalizePersonName(form.fullName))}
              spellCheck={false}
              required
            />
          )}
        </Field>

        <Grid cols={2} className="mt-4">
          <Field
            label="Подразделение"
            className="my-0"
            required={!departmentOptional}
            hint={
              departmentOptional
                ? `Можно не указывать — сотрудник попадёт в «${UNSPECIFIED_DEPARTMENT}»`
                : undefined
            }
            error={fieldError(error, 'departmentId') ?? fieldError(error, 'departmentName')}
          >
            {(props) =>
              derivedWorkplace ? (
                <Input id={props.id} value={derivedWorkplace.departmentName} disabled />
              ) : (
                // Единый паттерн выбора/создания подразделения (centrika-docs!70): акцентное
                // «+ Новое подразделение» вверху, ввод нового заменяет список полем и ссылкой
                // «Выбрать из списка». Новое подразделение создаётся вместе с сотрудником через
                // `newWorkplace.departmentName` (departmentId пуст).
                <DepartmentSelect
                  departments={departments}
                  value={form.department}
                  onSelect={(name, id) =>
                    setForm((f) => ({ ...f, department: name, departmentId: id ?? '' }))
                  }
                  deferCreate
                  entryPlaceholder={DEPARTMENT_SELECT_EMPLOYEE_PLACEHOLDER}
                  backToListLabel={DEPARTMENT_SELECT_CHOOSE_FROM_LIST}
                  id={props.id}
                  aria-required={props['aria-required']}
                />
              )
            }
          </Field>
          <Field label="Должность" className="my-0" required error={fieldError(error, 'position')}>
            {(props) =>
              derivedWorkplace ? (
                <Input id={props.id} value={derivedWorkplace.position} disabled />
              ) : (
                <PositionSelect
                  valueName={form.position}
                  // Берём только имя: тело §3b на пути создания места несёт свободный `position`
                  // (positionId не отправляется). Совпадение с существующей записью резолвит
                  // БЭКЕНД (employeePlaceResolve → каноническое имя), поэтому `_id` здесь не нужен.
                  onChange={(_id, name) => set('position', name)}
                  allowCreate
                  id={props.id}
                  required
                  invalid={props['aria-invalid']}
                  aria-required={props['aria-required']}
                />
              )
            }
          </Field>
        </Grid>

        <div className="mt-3 flex items-center gap-2.5 py-2">
          <Checkbox
            id="employee-is-shift"
            checked={form.isShift}
            onCheckedChange={(v) => set('isShift', v === true)}
          />
          <label htmlFor="employee-is-shift" className="text-13 text-txt">
            Сменное рабочее место — работает по сменам на одном месте с другими
          </label>
        </div>

        {form.isShift ? (
          <Field
            label="На одном рабочем месте с"
            // Блок виден только у сменного рабочего места, а там минимум один сменщик обязателен
            // (`validShiftPartners`), поэтому подпись всегда со звёздочкой.
            required
            className="my-0"
            hint="СОУТ и профриски у сменщиков общие — сотрудник добавится в существующую строку пульта; подразделение и должность возьмутся оттуда."
            error={shiftPartnerError}
          >
            {(props) =>
              partnerCandidates.length === 0 ? (
                <p className="py-2 text-13 text-txt-secondary">
                  Сначала добавьте других сотрудников, чтобы выбрать рабочее место.
                </p>
              ) : (
                // Группа чекбоксов: обязателен минимум один сменщик, а не конкретный чекбокс.
                // `aria-required` роль `group` не поддерживает, поэтому обязательность здесь
                // остаётся только визуальной — как и было до маркера.
                <div
                  id={props.id}
                  role="group"
                  aria-describedby={props['aria-describedby']}
                  className="max-h-40 space-y-1.5 overflow-y-auto rounded-9 border border-line p-2"
                >
                  {partnerCandidates.map((e) => (
                    <label key={e.id} className="flex items-center gap-2 py-0.5 text-13 text-txt">
                      <Checkbox
                        checked={form.shiftPartnerIds.includes(e.id)}
                        onCheckedChange={(v) => togglePartner(e.id, v === true)}
                      />
                      <span>
                        {e.fullName}
                        <span className="text-txt-tertiary">
                          {' '}
                          — {e.departmentName} · {e.position}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              )
            }
          </Field>
        ) : null}

        {/* Блок «Для документов — можно заполнить позже» (§3.2): все поля необязательные.
            Заголовок-сепаратор — стиль `.ae-sep` макета #addEmpModal (font 12/600, uppercase,
            letter-spacing, верхняя граница + отступы токенов). */}
        <div className="mt-4 border-t border-line pt-3.5">
          <h3 className="text-12 font-semibold uppercase tracking-wide text-txt-secondary">
            Для документов — можно заполнить позже
          </h3>
          <p className="mt-1 text-13 text-txt-secondary">
            Эти данные подставляются в заказы автоматически, чтобы не вводить их каждый раз.
          </p>

          <Grid cols={2} className="mt-3">
            <Field
              label="СНИЛС"
              className="my-0"
              hint="нужен для анкеты СОУТ"
              error={fieldError(error, 'snils') ?? snilsError}
            >
              {(props) => (
                <SnilsInput
                  {...props}
                  value={form.snils}
                  invalid={Boolean(snilsError)}
                  onChange={(snils) => set('snils', snils)}
                />
              )}
            </Field>
            <Field
              label="Дата рождения"
              className="my-0"
              hint={!birthError ? 'к работникам младше 18 лет — особые требования' : undefined}
              error={fieldError(error, 'birthDate') ?? birthError}
            >
              {(props) => (
                <DateInput
                  {...props}
                  value={form.birthDate}
                  invalid={props['aria-invalid'] || birthFuture}
                  onChange={(birthDate) => set('birthDate', birthDate)}
                />
              )}
            </Field>
          </Grid>

          <Grid cols={2} className="mt-3">
            <Field label="Пол" className="my-0">
              {() => (
                <div className="flex gap-2" role="group" aria-label="Пол">
                  <Button
                    type="button"
                    size="sm"
                    variant={form.gender === 'm' ? 'primary' : 'secondary'}
                    aria-pressed={form.gender === 'm'}
                    onClick={() => set('gender', 'm')}
                  >
                    Мужской
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant={form.gender === 'f' ? 'primary' : 'secondary'}
                    aria-pressed={form.gender === 'f'}
                    onClick={() => set('gender', 'f')}
                  >
                    Женский
                  </Button>
                </div>
              )}
            </Field>
            <Field
              label="Дата приёма на работу"
              className="my-0"
              hint={
                !hiredError
                  ? 'новичка нужно обучить охране труда в первые 60 дней — кабинет напомнит'
                  : undefined
              }
              error={hiredError}
            >
              {(props) => (
                <DateInput
                  {...props}
                  value={form.hiredOn}
                  invalid={props['aria-invalid'] || hiredFuture || hiredBeforeBirth}
                  onChange={(hiredOn) => set('hiredOn', hiredOn)}
                />
              )}
            </Field>
          </Grid>

          {/* Чекбокс «Есть инвалидность» СКРЫТ из интерфейса (centrika-docs, shared
              «Добавить нового сотрудника», MR !37 — решение заказчика: не собирать признак
              через форму, чтобы не попадать под повышенный контроль по персданным). Само
              поле в структуре данных остаётся: `form.hasDisability` по-прежнему
              инициализируется из карточки сотрудника и уходит обратно в запрос (см.
              `stateFromEmployee` и сборку `input` выше), поэтому правка сотрудника НЕ
              обнуляет уже сохранённое значение. */}

          {/* Email/телефон — наравне с остальными полями профиля (решение заказчика, D1/D4):
              необязательны, для единообразия с карточкой сотрудника. */}
          <Grid cols={2} className="mt-2">
            <Field label="Email" className="my-0" error={fieldError(error, 'email')}>
              {(props) => (
                <Input
                  {...props}
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                />
              )}
            </Field>
            <Field label="Телефон" className="my-0" error={fieldError(error, 'phone')}>
              {(props) => (
                <Input
                  {...props}
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                />
              )}
            </Field>
          </Grid>

          <Grid cols={2}>
            <Field label="Образование" className="my-0">
              {(props) => (
                <EducationSelect
                  id={props.id}
                  value={form.education}
                  onChange={(v) => set('education', v)}
                />
              )}
            </Field>
            <Field label="Разряд" className="my-0">
              {(props) => (
                <Input {...props} value={form.rank} onChange={(e) => set('rank', e.target.value)} />
              )}
            </Field>
          </Grid>
        </div>

        <div className="mt-4 flex items-center gap-2">
          <Button type="submit" disabled={submitting || !valid}>
            {submitting ? 'Сохранение…' : isEditing ? 'Сохранить изменения' : 'Создать сотрудника'}
          </Button>
          {onCancel ? (
            <Button type="button" variant="secondary" onClick={onCancel}>
              Отмена
            </Button>
          ) : null}
          {/* Футер #addEmpModal (макет): «* — обязательные поля». */}
          <span className="ml-auto text-12 text-txt-tertiary">* — обязательные поля</span>
        </div>
      </form>
    </Card>
  )
}

export interface EmployeesManagerProps {
  api: CompanyScopeApi
  /** Открыть карточку сотрудника по id (навигация у клиента / inline у команды). */
  onOpenEmployee: (id: string) => void
  /**
   * Журнал сроков обучения этого же клиента — источник колонки «Обучение». Ссылка обязана быть
   * стабильной (функция уровня модуля или `useCallback`), иначе журнал перезапрашивался бы на
   * каждый рендер. Без загрузчика колонка показывает прочерк.
   */
  loadTrainingJournal?: (signal: AbortSignal) => Promise<JournalResponse>
}

type TrainingRowStatus = JournalRow['rowStatus']

/** Статус обучения сотрудника — подпись и цвет как у счётчиков журнала сроков. */
const TRAINING_ROW_VIEW: Record<
  TrainingRowStatus,
  { label: string; variant: 'red' | 'amber' | 'green' | 'gray' }
> = {
  red: { label: 'Просрочено', variant: 'red' },
  amber: { label: 'Истекает скоро', variant: 'amber' },
  green: { label: 'Всё в порядке', variant: 'green' },
  gray: { label: 'Нет данных', variant: 'gray' },
}

/**
 * Полный список сотрудников для ВЛОЖЕННЫХ форм (issue #14): таблица теперь показывает
 * страницу из 20 записей, а выбор сменщиков в форме сотрудника обязан видеть всех — иначе
 * сменщик за пределами страницы стал бы недоступен. Одна выдача сервера ограничена 100
 * записями (`parsePage`), поэтому набор собирается страницами по `total`.
 *
 * Ограничитель `MAX_PAGES` защищает от бесконечного цикла, если сервер вернёт неожиданный
 * `total`: с ним запрашивается не более 5000 сотрудников.
 */
const ROSTER_FETCH_LIMIT = 100
const ROSTER_FETCH_MAX_PAGES = 50

async function fetchAllEmployees(api: CompanyScopeApi, signal: AbortSignal): Promise<Employee[]> {
  const all: Employee[] = []
  // Целевой размер берётся из ПЕРВОГО ответа и дальше не пересматривается: если параллельно
  // (другая вкладка, коллега) кого-то заведут или деактивируют, «плавающий» total оборвал бы
  // цикл на странице раньше и часть кандидатов пропала бы из выбора.
  let expected: number | null = null
  for (let i = 0; i < ROSTER_FETCH_MAX_PAGES; i++) {
    const res = await api.listEmployees(
      { limit: ROSTER_FETCH_LIMIT, offset: i * ROSTER_FETCH_LIMIT },
      signal,
    )
    all.push(...res.items)
    if (expected === null) expected = res.total
    if (all.length >= expected || res.items.length === 0) break
  }
  return all
}

export function EmployeesManager({
  api,
  onOpenEmployee,
  loadTrainingJournal,
}: EmployeesManagerProps) {
  // Статус обучения по сотруднику из журнала сроков. `null` — журнал не загружен (ещё грузится,
  // не удалось загрузить или не передан): тогда в колонке прочерк, а не «Нет данных» — иначе
  // сбой запроса выглядел бы как отсутствие обучения.
  const [trainingStatus, setTrainingStatus] = useState<ReadonlyMap<
    string,
    TrainingRowStatus
  > | null>(null)
  useEffect(() => {
    if (!loadTrainingJournal) return
    const controller = new AbortController()
    setTrainingStatus(null)
    loadTrainingJournal(controller.signal)
      .then((journal) => {
        if (controller.signal.aborted) return
        setTrainingStatus(new Map(journal.rows.map((row) => [row.personId, row.rowStatus])))
      })
      // Колонка — дополнение к списку: сбой журнала список не закрывает.
      .catch(() => undefined)
    return () => controller.abort()
  }, [loadTrainingJournal])
  const [employees, setEmployees] = useState<Employee[]>([])
  // Полный список — только для вложенной формы (выбор сменщиков); таблица живёт на `employees`.
  const [allEmployees, setAllEmployees] = useState<Employee[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  // Счётчик перезагрузки страницы после действия над сотрудником (создание, деактивация/
  // активация, issue #14 п.6): состав страницы и `total` перечитываются с сервера с учётом
  // действующих поиска и фильтров — локальная правка массива их не учитывает.
  const [reload, setReload] = useState(0)
  const [workplaces, setWorkplaces] = useState<Workplace[]>([])
  // Существующие подразделения — для поля «Подразделение» в CreateEmployeeForm (p3-sout-commission
  // §3.1/D4): строгий выбор из СУЩЕСТВУЮЩИХ записей, не из `employees`/`workplaces` (те могут не
  // содержать подразделение без ни одного рабочего места).
  const [departmentsList, setDepartmentsList] = useState<Department[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  // Поиск уходит на сервер с дебаунсом (эталон `/team/orders`): фильтрация загруженной
  // страницы на фронте не нашла бы сотрудника за её пределами.
  const [debouncedSearch, setDebouncedSearch] = useState('')
  /** Фильтр по подразделению — id записи справочника (серверный, issue #14). */
  const [department, setDepartment] = useState(ALL)
  const [showForm, setShowForm] = useState(false)
  // Роster по умолчанию (§9.2/design D6) не показывает деактивированных — тумблер
  // включает их обратно (с бейджем). Пикеры для НОВЫХ заказов их не видят никогда.
  const [showDeactivated, setShowDeactivated] = useState(false)
  const [toggling, setToggling] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  // Контроллер фонового рефреша мест после создания: отменяем при размонтировании,
  // чтобы не делать setState на размонтированном компоненте.
  const bgRefresh = useRef<AbortController | null>(null)
  useEffect(() => () => bgRefresh.current?.abort(), [])

  // Справочники (места/подразделения) отдаются целиком и от страницы ростера не зависят —
  // грузим их один раз на скоуп, а не при каждой смене страницы или фильтра.
  useEffect(() => {
    const controller = new AbortController()
    Promise.all([api.listWorkplaces(controller.signal), api.listDepartments(controller.signal)])
      .then(([wps, deps]) => {
        if (controller.signal.aborted) return
        setWorkplaces(wps.items)
        setDepartmentsList(deps.items)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [api])

  // Дебаунс поискового запроса — как в очереди заказов: очистка поля применяется сразу,
  // любое изменение запроса открывает первую страницу.
  useEffect(() => {
    const next = search.trim()
    if (next === '') {
      setDebouncedSearch('')
      setPage(1)
      return
    }
    const t = setTimeout(() => {
      setDebouncedSearch(next)
      setPage(1)
    }, 300)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    api
      .listEmployees(
        {
          q: debouncedSearch || undefined,
          department: department === ALL ? undefined : department,
          // Тумблер «Показать деактивированных» выключен — сервер отдаёт только активных
          // (design D6), поэтому и `total`, и страницы считаются по тому же набору.
          activeOnly: !showDeactivated,
          limit: PAGE_SIZE,
          offset: (page - 1) * PAGE_SIZE,
        },
        controller.signal,
      )
      .then((res) => {
        if (controller.signal.aborted) return
        // Страница «уехала» за пределы результатов (фильтр сузил ростер, сотрудник удалён
        // из выборки) — возвращаемся на первую.
        if (res.items.length === 0 && page > 1) {
          setPage(1)
          return
        }
        setEmployees(res.items)
        setTotal(res.total)
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setError(getErrorMessage(err, 'Не удалось загрузить сотрудников'))
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [api, debouncedSearch, department, showDeactivated, page, reload])

  // Полный список для вложенной формы (выбор сменщиков) — грузится при первом открытии
  // формы и обновляется после сохранения; ростер по страницам его не заменяет.
  useEffect(() => {
    if (!showForm) return
    const controller = new AbortController()
    fetchAllEmployees(api, controller.signal)
      .then((items) => {
        if (!controller.signal.aborted) setAllEmployees(items)
      })
      .catch(() => undefined)
    return () => controller.abort()
  }, [api, showForm, reload])

  const grouped = useMemo(() => {
    // Группировка сохранена (заголовок подразделения над своими строками). Группируется
    // ТОЛЬКО текущая страница: сервер сортирует по подразделению → должности → ФИО → id,
    // поэтому строки одного подразделения идут подряд и группа не разрывается внутри
    // страницы. Заголовки групп элементами страницы не считаются.
    const map = new Map<string, Employee[]>()
    for (const e of employees) {
      const arr = map.get(e.departmentName) ?? []
      arr.push(e)
      map.set(e.departmentName, arr)
    }
    return Array.from(map.entries())
  }, [employees])

  function onCreated(_employee: Employee, createdWorkplace: boolean) {
    // Перечитываем страницу с сервера (issue #14 п.6): новый сотрудник попадёт на своё место
    // в сортировке и учтётся в `total`; вставка в начало массива дала бы 21-ю строку на
    // странице и неверный счётчик страниц.
    setReload((n) => n + 1)
    if (createdWorkplace) {
      // Создано новое рабочее место — подтянем актуальный справочник мест в фон.
      bgRefresh.current?.abort()
      const controller = new AbortController()
      bgRefresh.current = controller
      api
        .listWorkplaces(controller.signal)
        .then((res) => {
          if (!controller.signal.aborted) setWorkplaces(res.items)
        })
        .catch(() => undefined)
    }
    setShowForm(false)
  }

  // Деактивация/активация (§10.3, design D1) — мягкая, обратимая; история не трогается.
  async function toggleActive(employee: Employee) {
    setActionError(null)
    setToggling(employee.id)
    try {
      const updated = employee.isActive
        ? await api.deleteEmployee(employee.id)
        : await api.activateEmployee(employee.id)
      setEmployees((prev) => prev.map((e) => (e.id === employee.id ? { ...e, ...updated } : e)))
      // При выключенном тумблере деактивированный сотрудник выпадает из выборки — состав
      // страницы и `total` перечитываем с сервера (issue #14 п.6).
      if (!showDeactivated) setReload((n) => n + 1)
    } catch (err) {
      setActionError(
        getErrorMessage(
          err,
          employee.isActive ? 'Не удалось деактивировать' : 'Не удалось активировать',
        ),
      )
    } finally {
      setToggling(null)
    }
  }

  return (
    <div>
      <div className="mb-4 flex items-center justify-end">
        <Button
          leftIcon={<UserPlus className="size-4 stroke-current" aria-hidden />}
          onClick={() => setShowForm(true)}
        >
          Добавить сотрудника
        </Button>
      </div>

      {/* Форма добавления сотрудника — в диалоговом окне поверх страницы (centrika-docs!97): в
          составе самой страницы её больше нет. Пока окно открыто, элементы страницы за ним
          недоступны (модальный Radix Dialog). Закрытие размонтирует форму, поэтому повторное
          открытие показывает исходные значения; ошибка создания окно не закрывает — введённые
          данные остаются в форме вместе с сообщением об ошибке. */}
      <Dialog
        open={showForm}
        onOpenChange={(next) => {
          if (!next) setShowForm(false)
        }}
      >
        <DialogContent title="Добавить сотрудника" className="max-w-3xl">
          <CreateEmployeeForm
            api={api}
            workplaces={workplaces}
            employees={allEmployees}
            departments={departmentsList}
            onCreated={onCreated}
            onCancel={() => setShowForm(false)}
            header="Добавить сотрудника"
          />
        </DialogContent>
      </Dialog>

      <Toolbar aria-label="Поиск и фильтр сотрудников">
        <div className="min-w-48 flex-1">
          <Input
            placeholder="Поиск по ФИО"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Поиск по ФИО"
          />
        </div>
        {/* Варианты фильтра — из справочника подразделений, а не из загруженной страницы
            ростера: страница показывает 20 сотрудников, и по её составу список отделов был
            бы неполным (issue #14). */}
        <Select
          value={department}
          onValueChange={(v) => {
            setDepartment(v)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-56" aria-label="Подразделение">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Все подразделения</SelectItem>
            {departmentsList.map((d) => (
              <SelectItem key={d.id} value={d.id}>
                {d.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 whitespace-nowrap">
          <span className="text-13 text-txt-secondary">Показать деактивированных</span>
          <Switch
            checked={showDeactivated}
            onCheckedChange={(v) => {
              setShowDeactivated(v)
              setPage(1)
            }}
            aria-label="Показать деактивированных"
          />
        </div>
      </Toolbar>

      {actionError ? (
        <Alert variant="danger" className="mt-3">
          {actionError}
        </Alert>
      ) : null}

      <Panel>
        {error ? (
          <Alert variant="danger" className="m-4">
            {error}
          </Alert>
        ) : loading ? (
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        ) : grouped.length === 0 ? (
          <EmptyState
            icon={UserPlus}
            title="Сотрудников пока нет"
            description="Добавьте первого сотрудника — отдел и должность можно создать прямо в форме"
          />
        ) : (
          <DataTable>
            <DataTableHead>
              <DataTableRow>
                <DataTableHeaderCell>Сотрудник</DataTableHeaderCell>
                <DataTableHeaderCell>Должность</DataTableHeaderCell>
                <DataTableHeaderCell>Статус</DataTableHeaderCell>
                <DataTableHeaderCell>Обучение</DataTableHeaderCell>
                <DataTableHeaderCell aria-label="Действия" />
              </DataTableRow>
            </DataTableHead>
            <DataTableBody>
              {grouped.map(([dept, rows]) => (
                <DataTableGroupFragment
                  key={dept}
                  dept={dept}
                  rows={rows}
                  onOpenEmployee={onOpenEmployee}
                  onToggleActive={toggleActive}
                  toggling={toggling}
                  trainingStatus={trainingStatus}
                />
              ))}
            </DataTableBody>
          </DataTable>
        )}
      </Panel>
      <Pagination page={page} totalPages={pageCount(total)} onChange={setPage} />
    </div>
  )
}

/** Статус обучения сотрудника в строке списка; пока журнал не загружен — прочерк. */
function TrainingStatusBadge({
  status,
  personId,
}: {
  status: ReadonlyMap<string, TrainingRowStatus> | null
  personId: string
}) {
  if (!status) return <span className="text-txt-tertiary">—</span>
  // Сотрудника нет в журнале — записей обучения у него нет.
  const view = TRAINING_ROW_VIEW[status.get(personId) ?? 'gray']
  return <Badge variant={view.variant}>{view.label}</Badge>
}

function DataTableGroupFragment({
  dept,
  rows,
  onOpenEmployee,
  onToggleActive,
  toggling,
  trainingStatus,
}: {
  dept: string
  rows: Employee[]
  onOpenEmployee: (id: string) => void
  onToggleActive: (employee: Employee) => void
  toggling: string | null
  trainingStatus: ReadonlyMap<string, TrainingRowStatus> | null
}) {
  return (
    <>
      <DataTableGroupRow colSpan={5}>{dept}</DataTableGroupRow>
      {rows.map((e) => (
        <DataTableRow
          key={e.id}
          clickable
          onClick={() => onOpenEmployee(e.id)}
          className={!e.isActive ? 'opacity-60' : undefined}
        >
          <DataTableCell>
            <span className="flex flex-wrap items-center gap-2 font-medium text-txt">
              {e.fullName}
              {e.isMinor ? <Badge variant="amber">несовершеннолетний</Badge> : null}
              {e.isShift ? <Badge variant="blue">сменное</Badge> : null}
            </span>
          </DataTableCell>
          <DataTableCell>{e.position}</DataTableCell>
          <DataTableCell>
            <Badge variant={e.isActive ? 'green' : 'gray'}>
              {e.isActive ? 'активен' : 'деактивирован'}
            </Badge>
          </DataTableCell>
          <DataTableCell>
            <TrainingStatusBadge status={trainingStatus} personId={e.id} />
          </DataTableCell>
          <DataTableCell>
            <Button
              size="sm"
              variant="secondary"
              disabled={toggling === e.id}
              onClick={(ev) => {
                ev.stopPropagation()
                onToggleActive(e)
              }}
            >
              {toggling === e.id ? 'Сохранение…' : e.isActive ? 'Уволен(а)' : 'Активировать'}
            </Button>
          </DataTableCell>
        </DataTableRow>
      ))}
    </>
  )
}
